/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { createContext, useContext, useMemo, useState } from 'react'
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'

import { ThemeProvider as StyledThemeProvider } from 'styled-components'

import { colors, mediaQuery, shadows, units } from '../app/Theme'
import type { MediaQuery, ThemeColors, ThemeShadows, ThemeUnits } from '../types/theme'

type ThemeContextType = {
  colors: ThemeColors
  darkMode: boolean
  mediaQuery: MediaQuery
  shadows: ThemeShadows
  units: ThemeUnits
  toggleDarkMode: Dispatch<SetStateAction<boolean>>
}

const INITIAL_THEME_CONTEXT: ThemeContextType = {
  colors: {} as ThemeColors,
  darkMode: false,
  mediaQuery: {} as MediaQuery,
  shadows: {} as ThemeShadows,
  units: {} as ThemeUnits,
  toggleDarkMode: () => {
    throw new Error('toggleDarkMode function must be overriden before use')
  }
}

const ThemeContext = createContext<ThemeContextType>(INITIAL_THEME_CONTEXT)

const ThemeContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const [darkMode, toggleDarkMode] = useState<boolean>(INITIAL_THEME_CONTEXT.darkMode)

  const defaultTheme = useMemo(
    () => ({
      colors: colors(darkMode),
      mediaQuery,
      shadows: shadows(darkMode),
      units
    }),
    [darkMode]
  )

  return (
    <ThemeContext.Provider value={{ darkMode, ...defaultTheme, toggleDarkMode }}>
      <StyledThemeProvider theme={{ darkMode, ...defaultTheme }}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  )
}

export const useThemeContext = () => useContext(ThemeContext)

export default ThemeContextProvider
